// src/utils/Onboarding/ImageResize.js

/**
 * Resize an image to a maximum long-edge of `maxLongEdge` pixels.
 * Returns { blob, width, height, mime } with JPEG output for consistency.
 *
 * Accepts: Blob | File | string (URL/dataURL)
 */
export async function resizeImageToMaxLongEdge(input, maxLongEdge = 1600, quality = 0.92, mime = 'image/jpeg') {
  const image = await loadImageEl(input);
  const { naturalWidth: w, naturalHeight: h } = image;

  if (!w || !h) throw new Error('Invalid image dimensions');

  const long = Math.max(w, h);
  if (long <= maxLongEdge) {
    // Still re-encode → consistent output & smaller payload; preserves EXIF? (no)
    const blob = await drawToBlob(image, w, h, quality, mime);
    return { blob, width: w, height: h, mime };
  }

  const scale = maxLongEdge / long;
  const outW = Math.round(w * scale);
  const outH = Math.round(h * scale);
  const blob = await drawToBlob(image, outW, outH, quality, mime);
  return { blob, width: outW, height: outH, mime };
}

async function loadImageEl(input) {
  let src;
  if (typeof input === 'string') {
    src = input;
  } else if (input instanceof Blob) {
    src = URL.createObjectURL(input);
  } else if (input?.file instanceof Blob) {
    src = URL.createObjectURL(input.file);
  } else if (input?.dataUrl) {
    src = input.dataUrl;
  } else if (input?.url) {
    src = input.url;
  } else if (input?.src) {
    src = input.src;
  } else {
    throw new Error('Unsupported image source');
  }

  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.loading = 'eager';
    await new Promise((res, rej) => {
      img.onload = () => res();
      img.onerror = (e) => rej(e);
      img.src = src;
    });
    return img;
  } finally {
    // If we made an objectURL above, the caller may revoke it after use if needed
  }
}

function drawToBlob(img, w, h, quality, mime) {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(img, 0, 0, w, h);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
      mime,
      quality
    );
  });
}
